import {
  Anchor,
  Avatar,
  Box,
  Card,
  Flex,
  Grid,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  Title,
} from "@mantine/core";

import profilePlaceholder from "../../assets/defaultSponsorProfile.png";

import {
  useGetPublicSponsors,
  useGetPublicSponsorLevels,
  useGetPublicSponsorReps,
} from "../../api/sponsors";
import { useState } from "react";
import { useDisclosure } from "@mantine/hooks";
import {
  IconBrandFacebook,
  IconBrandGithub,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconBrandTwitter,
  IconBrandYoutube,
  IconWorld,
  IconMail,
  IconLink,
} from "@tabler/icons-react";
import MultilineBadge from "../MultilineBadge";
import { useTranslation } from "react-i18next";
import { getI18nText } from "../../utils/i18nUtils";

const Sponsor = ({ eventId }) => {
  const [selectedSponsor, setSelectedSponsor] = useState(null);
  const { t, i18n } = useTranslation();

  const [opened, { open, close }] = useDisclosure(false);

  const { data: sponsors, isLoading: isLoadingSponsors } = useGetPublicSponsors(
    {
      eventId,
    }
  );
  const { data: sponsorLevels, isLoading: isLoadingSponsorLevels } =
    useGetPublicSponsorLevels({
      eventId,
    });
  const { data: sponsorReps, isLoading: isLoadingSponsorReps } =
    useGetPublicSponsorReps({ eventId });

  if (isLoadingSponsors || isLoadingSponsorLevels || isLoadingSponsorReps) {
    return (
      <Flex justify="center">
        <Loader color="blue" />
      </Flex>
    );
  }

  const iconMap = {
    linkedin: <IconBrandLinkedin />,
    twitter: <IconBrandTwitter />,
    facebook: <IconBrandFacebook />,
    instagram: <IconBrandInstagram />,
    youtube: <IconBrandYoutube />,
    github: <IconBrandGithub />,
    email: <IconMail />,
    website: <IconWorld />,
  };

  const groupedSponsors = sponsors?.data?.reduce((acc, sponsor) => {
    const level = sponsor?.levelName;
    if (!acc[level]) {
      acc[level] = [];
    }
    acc[level].push(sponsor);

    acc[level].sort((a, b) => {
      if (a.order !== b.order) return (a.order ?? 0) - (b.order ?? 0);
      return a.id - b.id;
    });
    return acc;
  }, Object.create(null));

  if (sponsors?.length === 0 || sponsorLevels?.length === 0) {
    return (
      <Title
        order={2}
        mb="lg"
        style={{
          color: "#667C99",
          textAlign: "center",
        }}
      >
        {t("no_sponsors_for_event")}
      </Title>
    );
  }

  const socialLinks = selectedSponsor?.links?.filter((link) => {
    return link.type === "social";
  });

  const externalLinks = selectedSponsor?.links?.filter(
    (link) => link.type === "external"
  );

  const getLinkProps = (link) => {
    const isEmail = link.platform === "email";

    return {
      href: isEmail ? `mailto:${link.url}` : link.url,
      target: isEmail ? undefined : "_blank",
      rel: isEmail ? undefined : "noopener noreferrer",
    };
  };

  const handleViewSponsor = (sponsor) => {
    sponsor.reps = sponsorReps.filter((rep) => rep.sponsorId === sponsor.id);
    setSelectedSponsor(sponsor);
    open();
  };
  return (
    <Stack gap="xl">
      {sponsorLevels
        ?.sort((a, b) => a.order - b.order)
        .map((level) => {
          if (!groupedSponsors[level.name]) return null;
          return (
            <Box key={level.id}>
              <Title
                order={2}
                mb="lg"
                style={{
                  color: "#667C99",
                  textAlign: "center",
                }}
              >
                {getI18nText(level.nameI18n, level.name, i18n)}
              </Title>
              <Grid gap="lg" justify="center">
                {groupedSponsors[level.name].map((sponsor) => (
                  <Grid.Col
                    key={sponsor.id}
                    span={{
                      base: 12,
                      sm: level.name === "Platinum" ? 6 : 6,
                      md: level.name === "Platinum" ? 6 : 4,
                    }}
                    h={270}
                  >
                    <Card
                      shadow="sm"
                      padding="lg"
                      radius="md"
                      withBorder
                      style={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        cursor: "pointer",
                      }}
                      onClick={() => handleViewSponsor(sponsor)}
                    >
                      <Stack gap="md" style={{ flex: 1 }} align="center">
                        <Avatar
                          src={sponsor.logo || profilePlaceholder}
                          alt={sponsor.name}
                          size={120}
                          radius="sm"
                        />
                        <Title
                          order={4}
                          style={{ textAlign: "center" }}
                          lineClamp={3}
                        >
                          {getI18nText(sponsor.nameI18n, sponsor.name, i18n)}
                        </Title>
                      </Stack>
                    </Card>
                  </Grid.Col>
                ))}
              </Grid>
            </Box>
          );
        })}
      {/* View Sponsor Modal */}
      <Modal
        opened={opened}
        onClose={close}
        title={
          <Title style={{ color: "#667C99" }}>{t("sponsor_details")}</Title>
        }
      >
        <Stack gap="md">
          <Card key={selectedSponsor?.id} padding="md" radius="md" withBorder>
            <Stack gap="md" align="center">
              <Avatar
                src={selectedSponsor?.logo || profilePlaceholder}
                size={80}
                radius="sm"
              />
              <Flex direction="column" ta="center" align="center" gap="md">
                <Text fw={600} size="sm">
                  {getI18nText(
                    selectedSponsor?.nameI18n,
                    selectedSponsor?.name,
                    i18n
                  )}
                </Text>
                <MultilineBadge
                  text={getI18nText(
                    selectedSponsor?.levelNameI18n,
                    selectedSponsor?.levelName,
                    i18n
                  )}
                />
              </Flex>
              <Text size="sm">
                {getI18nText(
                  selectedSponsor?.descriptionI18n,
                  selectedSponsor?.description,
                  i18n
                )}
              </Text>
              {selectedSponsor?.reps[0] && (
                <Text size="sm" c="gray">
                  {t("representatives")}:{" "}
                  {selectedSponsor.reps.map((rep, index) => (
                    <span key={rep.id}>
                      {rep.firstName}
                      {index < selectedSponsor.reps.length - 1 ? ", " : "."}
                    </span>
                  ))}
                </Text>
              )}
              {externalLinks && (
                <Flex wrap="wrap" gap="10" justify="space-evenly">
                  {externalLinks.map((link) => {
                    return (
                      <Anchor
                        component="a"
                        size="sm"
                        key={link.id}
                        c="dark"
                        {...getLinkProps(link)}
                      >
                        <Group gap={3}>
                          <IconLink size={20} />
                          <Text>{link.title}</Text>
                        </Group>
                      </Anchor>
                    );
                  })}
                </Flex>
              )}
              {socialLinks && (
                <Flex gap="sm">
                  {socialLinks.map((link) => (
                    <Anchor
                      component="a"
                      key={link.id}
                      c="dark"
                      {...getLinkProps(link)}
                    >
                      {iconMap[link.title?.toLowerCase()]}
                    </Anchor>
                  ))}
                </Flex>
              )}
            </Stack>
          </Card>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default Sponsor;
